import { useEffect } from "react";

// Per-route title, description and canonical url.
//
// What this can and cannot do, because the difference is easy to get wrong:
//
// Google renders JavaScript, so a title set here is the title it indexes, and
// it is also what the browser tab, the history entry and a bookmark show.
//
// WhatsApp, Instagram, LinkedIn and Facebook do NOT render JavaScript. They read
// the raw index.html and stop. Setting og:title or og:image from React would
// change nothing for them, so this hook deliberately does not touch the og:*
// tags — a link shared anywhere shows what public/index.html declares, and only
// that. Giving each route its own share card would need the HTML to be
// pre-rendered per route at build time; see the note at the end of this file.
//
// The canonical url matters here more than on a normal site: every route is
// served the same index.html by public/_redirects, so without it the whole site
// looks like one page duplicated under many urls.

const SITE_NAME = "RockFox Studio";

// These mirror public/index.html. If the tags there change, change them here —
// they are what a route hands back when it unmounts, so a page that sets no
// title of its own never inherits the previous page's.
const DEFAULT_TITLE = "RockFox Studio — Independent Creative Studio";
const DEFAULT_DESCRIPTION =
    "RockFox Studio — estúdio criativo especializado em Branding e Design. Criatividade que faz sua marca ser lembrada.";

const SITE_ORIGIN = "https://rockfoxstudio.netlify.app";

// Search results cut a description off around 155-160 characters. Trimming at a
// word boundary keeps the tail readable instead of ending mid-word.
const MAX_DESCRIPTION = 158;

export const trimDescription = (text) => {
    const clean = text.replace(/[“”"]/g, "").replace(/\s+/g, " ").trim();
    if (clean.length <= MAX_DESCRIPTION) return clean;
    const cut = clean.slice(0, MAX_DESCRIPTION);
    return `${cut.slice(0, cut.lastIndexOf(" "))}…`;
};

const setMeta = (name, content) => {
    let tag = document.head.querySelector(`meta[name="${name}"]`);
    if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", name);
        document.head.appendChild(tag);
    }
    tag.setAttribute("content", content);
};

// No path means no canonical — and the tag has to be REMOVED, not left alone.
// A 404 that keeps the previous route's canonical is telling search engines the
// missing page is really that other page, which is worse than saying nothing.
const setCanonical = (path) => {
    const existing = document.head.querySelector('link[rel="canonical"]');
    if (!path) {
        existing?.remove();
        return;
    }
    const tag = existing ?? document.createElement("link");
    tag.setAttribute("rel", "canonical");
    tag.setAttribute("href", `${SITE_ORIGIN}${path}`);
    if (!existing) document.head.appendChild(tag);
};

// ServicePage and CasePage render NotFound for an unknown slug, so three
// components describe the same screen. React runs a child's effect before its
// parent's, which means the parent has the last word — they have to agree, and
// sharing one object is how they agree by construction instead of by memory.
export const NOT_FOUND_META = {
    title: "Página não encontrada",
    description: "O endereço que você abriu não corresponde a nenhuma página do site.",
};

// `title` is the page's own name; the studio name is appended here so no call
// site can spell it differently. Pass null to keep the site-wide title, which is
// what the home wants — "Home — RockFox Studio" would be worse than the default.
export const usePageMeta = ({ title, description, path }) => {
    useEffect(() => {
        document.title = title ? `${title} — ${SITE_NAME}` : DEFAULT_TITLE;
        setMeta("description", description ? trimDescription(description) : DEFAULT_DESCRIPTION);
        setCanonical(path);

        return () => {
            document.title = DEFAULT_TITLE;
            setMeta("description", DEFAULT_DESCRIPTION);
        };
    }, [title, description, path]);
};

// Per-route share cards now exist, and they are not built here.
// scripts/build-share-cards.mjs runs after craco build and writes one
// index.html per route into build/, each with its own og:* and twitter:* tags.
// Netlify serves those real files and only falls back to /* -> /index.html for
// paths that have none.
//
// So there are two places a page's title lives, and they are not redundant:
// this hook owns what the browser and Google see, the build script owns what a
// link scraper sees. Changing one does not change the other — if a route's
// share card looks wrong, the file to edit is the script, not this one.
